import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Sparkles, Loader2, AlertCircle, Check, ChevronRight, Target, TrendingUp, FileText, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const LOADING_STEPS = [
  'Uploading your sample...',
  'Analyzing letter formations...',
  'Evaluating dimensions...',
  'Identifying issues & strengths...',
  'Building training plan...',
  'Generating your report...'
];

export default function AIAnalysis() {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateCredits } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const loadingIntervalRef = useRef(null);

  // Multi-step loading animation
  useEffect(() => {
    if (loading) {
      setLoadingStep(0);
      loadingIntervalRef.current = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < LOADING_STEPS.length - 1) return prev + 1;
          return prev;
        });
      }, 3000);
    } else {
      clearInterval(loadingIntervalRef.current);
    }
    return () => clearInterval(loadingIntervalRef.current);
  }, [loading]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(selectedFile.type)) {
        setError('Please select a JPEG, PNG, or WebP image');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError(null);

    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;

    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/analysis' } } });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/analysis/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      updateCredits(response.data.creditsRemaining);
      // Redirect to shareable analysis URL
      navigate(`/analysis/${response.data.publicId}?from=create`);

    } catch (err) {
      if (err.response?.status === 402) {
        setError('You need credits to analyze your handwriting. Please purchase a package.');
      } else if (err.response?.status === 401) {
        setError('Please log in to use AI analysis.');
      } else {
        setError(err.response?.data?.error || 'Analysis failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-bastion-blue-50 text-bastion-black px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            AI-Powered Analysis
          </div>
          <h1 className="section-title">Analyze Your Handwriting</h1>
          <p className="section-subtitle">
            Upload a sample of your handwriting and get instant AI-powered feedback
          </p>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="card text-center py-16 mb-8">
            <Loader2 className="w-12 h-12 animate-spin text-bastion-blue-500 mx-auto mb-6" />
            <p className="text-lg font-medium text-bastion-black mb-2">
              {LOADING_STEPS[loadingStep]}
            </p>
            <p className="text-sm text-bastion-gray-700">This usually takes 15-30 seconds</p>
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {LOADING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    i <= loadingStep ? 'bg-bastion-blue-500' : 'bg-bastion-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Upload */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upload Area */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Upload Handwriting Sample</h2>

              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  preview
                    ? 'border-bastion-black bg-bastion-black/5'
                    : 'border-bastion-gray-300 hover:border-bastion-black'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {preview ? (
                  <div>
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-64 mx-auto rounded-lg mb-4"
                    />
                    <p className="text-sm text-bastion-gray-700">
                      Click to change image
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-bastion-gray-400 mx-auto mb-4" />
                    <p className="text-bastion-gray-800 mb-2">
                      Drop your image here or click to browse
                    </p>
                    <p className="text-sm text-bastion-gray-700">
                      JPEG, PNG, or WebP (max 10MB)
                    </p>
                  </>
                )}
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {file && !error && (
                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="btn-primary w-full mt-4"
                >
                  <Sparkles className="w-5 h-5" />
                  {isAuthenticated ? 'Analyze Handwriting' : 'Log In to Analyze'}
                </button>
              )}

              {!isAuthenticated && (
                <p className="text-sm text-bastion-gray-700 text-center mt-4">
                  <Link to="/login" className="text-bastion-black hover:underline">
                    Log in
                  </Link>{' '}
                  or{' '}
                  <Link to="/register" className="text-bastion-black hover:underline">
                    sign up for a free account
                  </Link>{' '}
                  to analyze your handwriting
                </p>
              )}
            </div>

            {/* Info Panel */}
            <div className="space-y-6">
              <div className="card">
                <h2 className="text-lg font-semibold mb-4">What We Analyze</h2>
                <ul className="space-y-3">
                  {[
                    { name: 'Legibility', desc: 'How easy is your writing to read?' },
                    { name: 'Consistency', desc: 'Are your letters uniform in size and spacing?' },
                    { name: 'Fluency', desc: 'Does your writing flow smoothly?' },
                    { name: 'Structure', desc: 'Are letters properly formed and aligned?' },
                    { name: 'Pen Control', desc: 'How well do you control pressure and strokes?' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <span className="font-medium">{item.name}</span>
                        <p className="text-sm text-bastion-gray-700">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {isAuthenticated && (
                <div className="card bg-bastion-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-bastion-gray-700">Your Credits</p>
                      <p className="text-2xl font-bold text-bastion-black">{user?.credits || 0}</p>
                    </div>
                    <Link to="/pricing" className="btn-outline text-sm py-2 px-4">
                      Get More
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sample Report Preview */}
        <div className="mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sample Report Visual */}
            <div className="bg-bastion-gray-50 rounded-2xl p-6">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                {/* Overall Score */}
                <div>
                  <h4 className="text-sm font-semibold text-bastion-gray-800 uppercase tracking-wider mb-1">Overall Score</h4>
                  <div className="flex items-center gap-4">
                    <div className="relative w-24 h-24">
                      <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" stroke="#E5E5E5" strokeWidth="8" fill="none" />
                        <circle cx="50" cy="50" r="42" stroke="#2383E2" strokeWidth="8" fill="none" strokeDasharray="264" strokeDashoffset="74" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-bastion-black">72</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-lg font-bold text-bastion-black">Good Start</span>
                      <p className="text-sm text-bastion-gray-800">Your handwriting has a solid foundation</p>
                    </div>
                  </div>
                </div>

                {/* Dimensional Scores */}
                <div>
                  <h4 className="text-sm font-semibold text-bastion-gray-800 uppercase tracking-wider mb-3">Dimensional Analysis</h4>
                  <div className="space-y-2">
                    {[
                      { name: 'Legibility', score: 70, color: 'bg-bastion-black' },
                      { name: 'Consistency', score: 65, color: 'bg-bastion-gray-600' },
                      { name: 'Fluency', score: 85, color: 'bg-bastion-black' },
                      { name: 'Structure', score: 68, color: 'bg-bastion-gray-600' },
                      { name: 'Pen Control', score: 72, color: 'bg-bastion-black' }
                    ].map((dim) => (
                      <div key={dim.name}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-bastion-gray-700 font-medium">{dim.name}</span>
                          <span className="font-bold text-bastion-black">{dim.score}</span>
                        </div>
                        <div className="h-2 bg-bastion-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${dim.color} rounded-full`} style={{ width: `${dim.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Writing Highlights */}
                <div>
                  <h4 className="text-sm font-semibold text-bastion-gray-800 uppercase tracking-wider mb-2">Writing Highlights</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-bastion-gray-50 rounded-lg p-2 text-xs text-bastion-gray-700">Good overall flow and rhythm in connections</div>
                    <div className="bg-bastion-gray-50 rounded-lg p-2 text-xs text-bastion-gray-700">Consistent slant maintained throughout the text</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="section-title">AI Handwriting Analysis &mdash; Sample Report</h2>
              <p className="text-bastion-gray-700 mt-4 text-lg leading-relaxed">
                Improve handwriting legibility and neatness with an evidence-based report.
                We analyze <strong>legibility</strong>, <strong>consistency</strong>, <strong>fluency</strong>,
                <strong> structure/alignment</strong>, and <strong>pen control</strong> to help you write neatly and with clarity.
              </p>
              <ul className="mt-6 space-y-3 text-bastion-gray-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-bastion-black mt-0.5 flex-shrink-0" />
                  <span>Overall score with peer benchmark and clear explanation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-bastion-black mt-0.5 flex-shrink-0" />
                  <span>Detailed scoring across 5 key dimensions with radar chart</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-bastion-black mt-0.5 flex-shrink-0" />
                  <span>Writing issues diagnosed with causes and impact</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-bastion-black mt-0.5 flex-shrink-0" />
                  <span>Personalized font recommendations for practice</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-bastion-black mt-0.5 flex-shrink-0" />
                  <span>7-day training plan with downloadable worksheets</span>
                </li>
              </ul>
              <Link to="/pricing" className="btn-primary mt-6">
                Learn how scoring works
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-16">
          <h2 className="section-title text-center mb-8">How AI Handwriting Analysis Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card text-center">
              <div className="w-14 h-14 bg-bastion-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Upload className="w-7 h-7 text-bastion-black" />
              </div>
              <h3 className="font-bold text-base mb-2">1. Upload Your Sample</h3>
              <p className="text-bastion-gray-700">
                Take a clear photo of your handwriting on paper. Our AI works best with good lighting and a flat surface.
              </p>
            </div>
            <div className="card text-center">
              <div className="w-14 h-14 bg-bastion-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-bastion-black" />
              </div>
              <h3 className="font-bold text-base mb-2">2. AI Analyzes</h3>
              <p className="text-bastion-gray-700">
                Our advanced AI examines your handwriting across 5 key dimensions: legibility, consistency, fluency, structure, and pen control.
              </p>
            </div>
            <div className="card text-center">
              <div className="w-14 h-14 bg-bastion-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-bastion-black" />
              </div>
              <h3 className="font-bold text-base mb-2">3. Get Your 12-Point Report</h3>
              <p className="text-bastion-gray-700">
                Receive a comprehensive diagnosis with scores, issues, strengths, font recommendations, and a personalized 7-day training plan.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h2 className="section-title text-center mb-8">Why Use AI Handwriting Analysis?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card flex gap-4">
              <div className="w-12 h-12 bg-bastion-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-bastion-black" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Identify Specific Weaknesses</h3>
                <p className="text-bastion-gray-800 text-sm">
                  Don't waste time practicing what you're already good at. Our AI pinpoints exactly what needs work so you can focus your efforts.
                </p>
              </div>
            </div>
            <div className="card flex gap-4">
              <div className="w-12 h-12 bg-bastion-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-bastion-black" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Track Your Progress</h3>
                <p className="text-bastion-gray-800 text-sm">
                  Analyze your handwriting periodically to see how much you've improved. Compare scores over time in your dashboard.
                </p>
              </div>
            </div>
            <div className="card flex gap-4">
              <div className="w-12 h-12 bg-bastion-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-bastion-black" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Targeted Practice Sheets</h3>
                <p className="text-bastion-gray-800 text-sm">
                  After analysis, use our free worksheet tools to create practice sheets that target your specific areas of improvement.
                </p>
              </div>
            </div>
            <div className="card flex gap-4">
              <div className="w-12 h-12 bg-bastion-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-bastion-black" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Expert-Level Feedback</h3>
                <p className="text-bastion-gray-800 text-sm">
                  Get the kind of detailed, actionable feedback that would normally require a handwriting instructor or occupational therapist.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="section-title text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            <FAQItem
              question="What kind of handwriting sample should I upload?"
              answer="For best results, upload a photo of at least 2-3 sentences of your natural handwriting on lined or unlined paper. Make sure the lighting is good, the paper is flat, and all your writing is clearly visible in the frame. Avoid shadows and try to capture the full sample without cutting off any text."
            />
            <FAQItem
              question="How accurate is the AI analysis?"
              answer="Our AI has been trained on thousands of handwriting samples and uses the same criteria that professional handwriting instructors use. While no AI is perfect, our analysis provides a reliable baseline for tracking your progress and identifying areas that need improvement."
            />
            <FAQItem
              question="How many credits does each analysis cost?"
              answer="Each handwriting analysis costs 1 credit. You can purchase credit packages starting at just $6.99 for a single analysis, with better per-analysis rates for larger packages. Teachers and schools can get rates as low as $1.66 per analysis."
            />
            <FAQItem
              question="Can I analyze children's handwriting?"
              answer="Yes! Our AI can analyze handwriting from writers of all ages. For children, the analysis will provide age-appropriate feedback and recommendations. It's a great tool for parents and teachers to identify specific areas where a child needs extra practice."
            />
            <FAQItem
              question="What are the five dimensions you analyze?"
              answer="We analyze: (1) Legibility - how easy your writing is to read; (2) Consistency - uniformity in letter size, spacing, and slant; (3) Fluency - the smoothness and flow of your writing; (4) Structure - proper letter formation and baseline alignment; (5) Pen Control - pressure consistency and stroke precision."
            />
            <FAQItem
              question="What's included in the 12-point diagnosis report?"
              answer="Your report includes: overall score with percentile ranking, radar chart visualization, detailed 5-dimension scoring with feedback, writing issues with causes and impact, process issues, your strengths, recommended fonts for practice, a personalized 7-day training plan, and downloadable worksheets for each training day."
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 card bg-bastion-black text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Improve Your Handwriting?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Upload your first sample and get personalized feedback in seconds. Combine with our free worksheet tools for the fastest improvement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <Link to="/register" className="bg-white text-bastion-black hover:bg-bastion-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center justify-center gap-2">
                  Get Started Free
                  <ChevronRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="bg-transparent text-white border-2 border-white hover:bg-white/10 font-semibold py-3 px-6 rounded-lg transition-colors">
                  View Pricing
                </Link>
              </>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-white text-bastion-black hover:bg-bastion-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Upload Your Sample
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-semibold text-bastion-black pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-bastion-gray-700 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <p className="mt-4 text-bastion-gray-800 border-t border-bastion-gray-200 pt-4">
          {answer}
        </p>
      )}
    </div>
  );
}
